import React from 'react';

/**
 * HTML Content Renderer Utility
 * Safely renders HTML content with proper sanitization and fallbacks
 */

export interface HTMLRendererOptions {
  className?: string;
  prose?: boolean;
  maxWidth?: string;
  allowScripts?: boolean;
}

/**
 * Safely renders HTML content
 * @param content - The HTML content to render
 * @param options - Rendering options
 * @returns React element with rendered HTML or fallback text
 */
export const renderHTMLContent = (
  content: string | null | undefined,
  options: HTMLRendererOptions = {}
): React.ReactElement | null => {
  if (!content) return null;

  const {
    className = "prose prose-sm max-w-none break-words overflow-hidden",
    prose = true,
    maxWidth = "none",
    allowScripts = false
  } = options;

  // Check if content contains HTML tags
  if (content.includes("<") && content.includes(">")) {
    // Basic HTML sanitization (remove script tags and dangerous attributes)
    let sanitizedContent = content;
    
    if (!allowScripts) {
      // Remove script tags and their content
      sanitizedContent = sanitizedContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
      // Remove onclick and other event handlers
      sanitizedContent = sanitizedContent.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
      // Remove javascript: URLs
      sanitizedContent = sanitizedContent.replace(/javascript:/gi, '');
    }

    // Apply prose classes if requested
    const finalClassName = prose ? className : className.replace(/prose\s+prose-\w+/g, '').trim();

    return (
      <div
        className={finalClassName}
        style={{ maxWidth: maxWidth === "none" ? undefined : maxWidth }}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    );
  }

  // For plain text, wrap in paragraph with proper text handling
  return (
    <p className="text-gray-700 leading-relaxed break-words overflow-hidden">
      {content}
    </p>
  );
};

/**
 * Gets clean text from HTML content (removes all HTML tags)
 * @param htmlContent - The HTML content to clean
 * @returns Clean text without HTML tags
 */
export const getCleanText = (htmlContent: string | null | undefined): string => {
  if (!htmlContent) return "";
  return htmlContent.replace(/<[^>]*>/g, "");
};

/**
 * Truncates HTML content to a specified length while preserving HTML structure
 * @param htmlContent - The HTML content to truncate
 * @param maxLength - Maximum length in characters
 * @returns Truncated HTML content
 */
export const truncateHTML = (htmlContent: string | null | undefined, maxLength: number): string => {
  if (!htmlContent) return "";
  
  const cleanText = getCleanText(htmlContent);
  if (cleanText.length <= maxLength) return htmlContent;
  
  // Simple truncation - find the last complete word within the limit
  const truncatedText = cleanText.substring(0, maxLength).replace(/\s+\S*$/, '');
  return truncatedText + '...';
};

/**
 * Checks if content contains HTML tags
 * @param content - The content to check
 * @returns True if content contains HTML tags
 */
export const containsHTML = (content: string | null | undefined): boolean => {
  if (!content) return false;
  return content.includes("<") && content.includes(">");
};

/**
 * Renders HTML content with a specific prose size
 * @param content - The HTML content to render
 * @param size - The prose size (xs, sm, base, lg, xl, 2xl)
 * @returns React element with rendered HTML
 */
export const renderHTMLWithProse = (
  content: string | null | undefined,
  size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' = 'base'
): React.ReactElement | null => {
  return renderHTMLContent(content, {
    className: `prose prose-${size} max-w-none break-words overflow-hidden`,
    prose: true
  });
};
